import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubmitNewsPage() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ title: '', category_id: '', body: '', contact_email: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/categories`).then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim() || !form.contact_email.trim()) { toast.error('Molimo ispunite sva obavezna polja'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/public/news-submissions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { setSent(true); toast.success('Vijest uspješno predana!'); }
      else { toast.error('Greška pri slanju'); }
    } catch (e) { toast.error('Greška: ' + e.message); }
    finally { setSending(false); }
  }

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="submit-success">
        <div className="text-5xl mb-4">&#10004;</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Hvala!</h1>
        <p className="text-gray-500">Vaša vijest je uspješno predana. Naš tim će je pregledati u najkraćem mogućem roku.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>{`Predaj svoju vijest \u2014 e-mina.eu`}</title></Helmet>
      <div className="max-w-2xl mx-auto px-4 py-10" data-testid="submit-news-page">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Predaj svoju vijest!</h1>
          <p className="text-gray-500">Vidim, čujem, znam... Predaj vijest i budi promjena koju želiš vidjeti.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5 bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Naslov vijesti *</label>
            <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400" placeholder="Unesite naslov vijesti" data-testid="submit-title" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kategorija</label>
            <select value={form.category_id} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none" data-testid="submit-category">
              <option value="">-- Odaberite kategoriju --</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name_hr}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sadržaj vijesti *</label>
            <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} rows={6} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400 resize-y" placeholder="Opišite vijest..." data-testid="submit-body" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kontakt e-mail *</label>
            <input type="email" value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400" placeholder="vas@email.com" data-testid="submit-email" required />
          </div>
          <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50" data-testid="submit-news-form-btn">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            {sending ? 'Šaljem...' : 'Predaj vijest'}
          </button>
        </form>
      </div>
    </>
  );
}
